import React, { useState, useEffect, useCallback } from 'react';
import useUrlState from '../../hooks/useUrlState';
import { useAuth } from '../../auth/AuthProvider';
import Header from '../../components/Header';
import KpiCard from '../../components/KpiCard';
import { colors, radius, shadows } from '../../styles/theme';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// ── Fatura Kalemleri Alt Component'i ──
const FaturaKalemleri = ({ detayData, formatCurrency }) => {
  // Gelen/Giden e-fatura: HTML görüntüsü
  if (detayData.html_base64) {
    try {
      // UTF-8 base64 decode (atob bozar, TextDecoder kullan)
      const bytes = Uint8Array.from(atob(detayData.html_base64), c => c.charCodeAt(0));
      const html = new TextDecoder('utf-8').decode(bytes);
      return (
        <div>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#333' }}>📄 E-Fatura Görüntüsü</h4>
          <iframe
            title="E-Fatura"
            srcDoc={html}
            style={{
              width: '100%',
              height: '500px',
              border: `1px solid ${colors.border}`,
              borderRadius: radius.md,
              background: colors.surface
            }}
            sandbox="allow-same-origin allow-scripts"
          />
        </div>
      );
    } catch {
      return <div style={{ color: colors.textTertiary, padding: '12px' }}>E-fatura görüntüsü yüklenemedi.</div>;
    }
  }

  // Gider fatura: Yapısal kalem tablosu
  const lines = detayData.invoiceLines || detayData.lines || detayData.items || [];
  if (lines.length === 0) {
    return <div style={{ color: colors.textTertiary, padding: '8px', textAlign: 'center' }}>Fatura kalemi bulunamadı.</div>;
  }

  const safeStr = (val) => {
    if (val === null || val === undefined) return '-';
    if (typeof val === 'object') return val.name || val.code || JSON.stringify(val);
    return String(val);
  };

  return (
    <div>
      <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#333' }}>📦 Fatura Kalemleri ({lines.length})</h4>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
        <thead>
          <tr style={{ background: colors.backgroundAlt, borderBottom: `2px solid ${colors.border}` }}>
            <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: colors.textSecondary }}>Ürün / Hizmet</th>
            <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: colors.textSecondary }}>Kod</th>
            <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, color: colors.textSecondary }}>Miktar</th>
            <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: colors.textSecondary }}>Birim</th>
            <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, color: colors.textSecondary }}>Birim Fiyat</th>
            <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, color: colors.textSecondary }}>Tutar</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, idx) => {
            const product = line.product || {};
            const urunAdi = safeStr(product.name || line.productName || line.description);
            const urunKodu = safeStr(product.code || line.productCode);
            const miktar = line.amount || line.quantity || 0;
            const birim = typeof line.unit === 'object' ? (line.unit?.name || line.unit?.code || '-') : safeStr(line.unit);
            const birimFiyat = line.price || line.unitPrice || 0;
            const lineToplam = line.total || line.lineTotal || (miktar * birimFiyat);
            const isHizmet = (product.type === 2);

            return (
              <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '8px 10px' }}>
                  {urunAdi}
                  {isHizmet && (
                    <span style={{
                      marginLeft: '6px', padding: '1px 6px', borderRadius: '3px',
                      background: '#e3f2fd', color: '#1565c0', fontSize: '10px', fontWeight: 600
                    }}>Hizmet</span>
                  )}
                </td>
                <td style={{ padding: '8px 10px', color: '#888', fontSize: '12px' }}>{urunKodu}</td>
                <td style={{ padding: '8px 10px', textAlign: 'right' }}>{miktar}</td>
                <td style={{ padding: '8px 10px', color: '#888' }}>{birim}</td>
                <td style={{ padding: '8px 10px', textAlign: 'right' }}>{formatCurrency(birimFiyat)}</td>
                <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(lineToplam)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Toplam Bilgisi */}
      <div style={{
        borderTop: `2px solid ${colors.border}`, marginTop: '8px', paddingTop: '12px',
        display: 'flex', justifyContent: 'flex-end', gap: '24px', fontSize: '13px'
      }}>
        {detayData.subTotal != null && (
          <div><span style={{ color: '#888' }}>Ara Toplam:</span> <strong>{formatCurrency(detayData.subTotal)}</strong></div>
        )}
        {(detayData.total != null && detayData.subTotal != null && detayData.total !== detayData.subTotal) && (
          <div><span style={{ color: '#888' }}>KDV:</span> <strong>{formatCurrency(detayData.total - detayData.subTotal)}</strong></div>
        )}
        {detayData.total != null && (
          <div><span style={{ color: '#888' }}>Genel Toplam:</span> <strong style={{ color: '#1976d2' }}>{formatCurrency(detayData.total)}</strong></div>
        )}
      </div>
    </div>
  );
};


const LogoFaturalarPage = () => {
  useAuth();

  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [activeTab, setActiveTab] = useUrlState('tab', 'gelen');
  const [faturalar, setFaturalar] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFatura, setSelectedFatura] = useState(null);
  const [detayData, setDetayData] = useState(null);
  const [detayLoading, setDetayLoading] = useState(false);

  // Tarih filtreleri
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [baslangic, setBaslangic] = useState(firstOfMonth.toISOString().split('T')[0]);
  const [bitis, setBitis] = useState(today.toISOString().split('T')[0]);

  // Sayfalama
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 50;

  // Bağlantı durumu
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await fetch(`${API_URL}/api/logo/status`);
        if (res.ok) {
          setStatus(await res.json());
        }
      } catch {
        setStatus({ connected: false, message: 'Sunucuya bağlanılamadı' });
      } finally {
        setStatusLoading(false);
      }
    };
    checkStatus();
  }, []);

  // Fatura çek
  const fetchFaturalar = useCallback(async () => {
    if (!status || !status.connected) return;
    setLoading(true);
    try {
      const endpoint = activeTab === 'gelen' ? 'gelen' : activeTab === 'giden' ? 'giden' : 'gider';
      const url = `${API_URL}/api/logo/faturalar/${endpoint}?baslangic=${baslangic}&bitis=${bitis}&page=${page}&page_size=${pageSize}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data)) {
          setFaturalar(data);
          setTotalCount(data.length);
        } else if (data && typeof data === 'object') {
          // Logo API: { count: N, data: [...], extraData: {...} }
          const items = data.data || data.items || data.records || [];
          setFaturalar(Array.isArray(items) ? items : []);
          setTotalCount(data.count || data.totalCount || (Array.isArray(items) ? items.length : 0));
        }
      } else {
        setFaturalar([]);
      }
    } catch (err) {
      logError('Fatura yüklenemedi:', err);
      setFaturalar([]);
    } finally {
      setLoading(false);
    }
  }, [status, activeTab, baslangic, bitis, page]);

  useEffect(() => {
    if (status?.connected) {
      fetchFaturalar();
    }
  }, [fetchFaturalar, status]);

  // Tab değiştiğinde sayfa sıfırla
  useEffect(() => {
    setPage(1);
    setFaturalar([]);
  }, [activeTab]);

  // Fatura detay (gider: id ile, gelen/giden: uuid ile)
  const fetchDetay = async (fatura, tab) => {
    setDetayLoading(true);
    setDetayData(null);
    try {
      let url;
      if (tab === 'gider') {
        const faturaId = fatura.id || fatura.invoiceId;
        url = `${API_URL}/api/logo/faturalar/gider/${faturaId}`;
      } else {
        const uuid = fatura.uuId || fatura.uuid || fatura.ettn;
        url = `${API_URL}/api/logo/faturalar/efatura/${uuid}`;
      }
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (res.ok) {
        setDetayData(await res.json());
      }
    } catch (err) {
      logError('Detay yüklenemedi:', err);
    } finally {
      setDetayLoading(false);
    }
  };

  // Hızlı tarih seçiciler
  const setThisMonth = () => {
    setBaslangic(firstOfMonth.toISOString().split('T')[0]);
    setBitis(today.toISOString().split('T')[0]);
    setPage(1);
  };

  const setLastMonth = () => {
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const end = new Date(today.getFullYear(), today.getMonth(), 0);
    setBaslangic(start.toISOString().split('T')[0]);
    setBitis(end.toISOString().split('T')[0]);
    setPage(1);
  };

  const setQuickRange = (months) => {
    const end = new Date();
    const start = new Date(end.getFullYear(), end.getMonth() - months + 1, 1);
    setBaslangic(start.toISOString().split('T')[0]);
    setBitis(end.toISOString().split('T')[0]);
    setPage(1);
  };

  // Format helpers
  const formatCurrency = (val) => {
    if (!val && val !== 0) return '-';
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(val);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('tr-TR');
    } catch {
      return dateStr;
    }
  };

  // Loading durumu
  if (statusLoading) {
    return (
      <div style={{ padding: '32px' }}>
        <Header title="Logo Faturalar" subtitle="Logo İşbaşı fatura verilerini görüntüleyin" />
        <div style={{ textAlign: 'center', padding: '60px', color: colors.textSecondary }}>
          ⏳ Logo İşbaşı bağlantısı kontrol ediliyor...
        </div>
      </div>
    );
  }

  // Bağlantı yok
  if (!status?.connected) {
    return (
      <div style={{ padding: '32px' }}>
        <Header title="Logo Faturalar" subtitle="Logo İşbaşı fatura verilerini görüntüleyin" />
        <div style={{
          background: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: radius.lg,
          padding: '24px',
          margin: '24px 0',
          display: 'flex',
          alignItems: 'center',
          gap: '16px'
        }}>
          <span style={{ fontSize: '32px' }}>⚠️</span>
          <div>
            <h3 style={{ margin: '0 0 8px 0', color: '#856404' }}>Logo İşbaşı Bağlantısı Yok</h3>
            <p style={{ margin: 0, color: '#856404' }}>
              {status?.message || 'Logo İşbaşı API bilgileri yapılandırılmamış.'}
            </p>
            <p style={{ margin: '8px 0 0 0', color: '#856404', fontSize: '13px' }}>
              Backend .env dosyasına LOGO_ISBASI_API_KEY, LOGO_ISBASI_USERNAME ve LOGO_ISBASI_PASSWORD ekleyin.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'gelen', label: '📥 Gelen Faturalar', desc: 'Alış e-faturaları' },
    { id: 'giden', label: '📤 Giden Faturalar', desc: 'Satış e-faturaları' },
    { id: 'gider', label: '🧾 Gider Faturaları', desc: 'Logo gider faturaları' },
  ];

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div style={{ padding: '32px' }}>
      <Header title="Logo Faturalar" subtitle="Logo İşbaşı fatura verilerini görüntüleyin" />

      {/* Bağlantı OK badge */}
      <div style={{
        background: '#d4edda',
        border: '1px solid #28a745',
        borderRadius: radius.md,
        padding: '8px 16px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '20px',
        fontSize: '13px',
        color: '#155724'
      }}>
        ✅ Logo İşbaşı bağlantısı aktif
      </div>

      {/* Tab Toggle */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: radius.md,
              cursor: 'pointer',
              fontWeight: activeTab === tab.id ? 600 : 400,
              background: activeTab === tab.id ? '#1976d2' : '#fff',
              color: activeTab === tab.id ? '#fff' : '#333',
              boxShadow: activeTab === tab.id ? '0 2px 8px rgba(25,118,210,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
              fontSize: '14px',
              transition: 'all 0.2s'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filtre Barı */}
      <div style={{
        background: colors.surface,
        borderRadius: radius.lg,
        padding: '16px 20px',
        boxShadow: shadows.sm,
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap'
      }}>
        <button onClick={setThisMonth} style={quickBtnStyle}>Bu Ay</button>
        <button onClick={setLastMonth} style={quickBtnStyle}>Geçen Ay</button>
        <button onClick={() => setQuickRange(3)} style={quickBtnStyle}>Son 3 Ay</button>
        <button onClick={() => setQuickRange(6)} style={quickBtnStyle}>Son 6 Ay</button>

        <div style={{ width: '1px', height: '24px', background: '#ddd', margin: '0 4px' }} />

        <input
          type="date"
          value={baslangic}
          onChange={(e) => { setBaslangic(e.target.value); setPage(1); }}
          style={dateInputStyle}
        />
        <span style={{ color: colors.textTertiary }}>—</span>
        <input
          type="date"
          value={bitis}
          onChange={(e) => { setBitis(e.target.value); setPage(1); }}
          style={dateInputStyle}
        />

        <button
          onClick={fetchFaturalar}
          style={{
            padding: '8px 20px',
            background: '#1976d2',
            color: '#fff',
            border: 'none',
            borderRadius: radius.md,
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '13px'
          }}
        >
          🔄 Faturaları Getir
        </button>
      </div>

      {/* KPI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
        <KpiCard label="Toplam Fatura" value={totalCount} icon="📄" />
        <KpiCard label="Aktif Sekme" value={tabs.find(t => t.id === activeTab)?.desc || ''} icon="📋" />
        <KpiCard label="Tarih Aralığı" value={`${formatDate(baslangic)} - ${formatDate(bitis)}`} icon="📅" />
      </div>

      {/* Fatura Tablosu */}
      <div style={{
        background: colors.surface,
        borderRadius: radius.lg,
        boxShadow: shadows.sm,
        overflow: 'hidden'
      }}>
        <div style={{ padding: '16px 20px', borderBottom: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>
            {tabs.find(t => t.id === activeTab)?.label} ({totalCount})
          </h3>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: colors.textSecondary }}>⏳ Faturalar yükleniyor...</div>
        ) : faturalar.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: colors.textTertiary }}>
            Bu tarih aralığında fatura bulunamadı.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: colors.backgroundAlt, borderBottom: `2px solid ${colors.border}` }}>
                  <th style={thStyle}>Fatura No</th>
                  <th style={thStyle}>Tarih</th>
                  <th style={thStyle}>Cari / Firma</th>
                  <th style={thStyle}>Tutar</th>
                  <th style={thStyle}>KDV</th>
                  <th style={thStyle}>Toplam</th>
                  <th style={thStyle}>Durum</th>
                  <th style={thStyle}>Detay</th>
                </tr>
              </thead>
              <tbody>
                {faturalar.map((f, i) => {
                  // Logo API field mapping (gelen: invoiceId/supplier, gider: number/firmName)
                  const faturaNo = f.invoiceId || f.invoiceNumber || f.number || f.no || '-';
                  const tarih = f.issueDate || f.date || '';
                  const cari = f.supplier || f.firmName || f.senderTitle || f.receiverTitle || f.name || '-';
                  // Gelen/Giden: amount=KDV dahil toplam, totalVatBase=KDV matrahı (net)
                  // Gider: total/grandTotal=toplam, grossTotal/taxableAmount=net
                  const toplam = f.amount || f.totalAmount || f.grandTotal || f.total || 0;
                  const net = f.totalVatBase || f.netAmount || f.grossTotal || f.taxableAmount || 0;
                  const kdv = net > 0 ? (toplam - net) : 0;
                  const tutar = net > 0 ? net : toplam;
                  const paraBirimi = f.currency || 'TL';
                  const durum = f.status || f.invoiceStatus || '-';
                  const faturaId = f.id || f.invoiceId || f.uuId || i;

                  return (
                    <tr
                      key={faturaId}
                      style={{
                        borderBottom: '1px solid #f0f0f0',
                        cursor: activeTab === 'gider' ? 'pointer' : 'default',
                        transition: 'background 0.15s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#f5f8ff'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={tdStyle}>{faturaNo}</td>
                      <td style={tdStyle}>{formatDate(tarih)}</td>
                      <td style={{ ...tdStyle, maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cari}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{formatCurrency(tutar)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{formatCurrency(kdv)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatCurrency(toplam)} {paraBirimi !== 'TRY' && paraBirimi !== 'TL' ? paraBirimi : ''}</td>
                      <td style={tdStyle}>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: radius.sm,
                          fontSize: '11px',
                          fontWeight: 600,
                          background: durum === 'Onaylandı' || durum === 'Approved' ? '#d4edda' : '#e3f2fd',
                          color: durum === 'Onaylandı' || durum === 'Approved' ? '#155724' : '#1565c0'
                        }}>{durum}</span>
                      </td>
                      <td style={tdStyle}>
                        <button
                          onClick={() => {
                            setSelectedFatura(f);
                            fetchDetay(f, activeTab);
                          }}
                          style={{
                            padding: '4px 12px',
                            background: colors.background,
                            border: `1px solid ${colors.border}`,
                            borderRadius: radius.sm,
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          🔍 Detay
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Sayfalama */}
        {totalPages > 1 && (
          <div style={{ padding: '12px 20px', borderTop: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'center', gap: '8px' }}>
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page <= 1}
              style={pageBtnStyle}
            >
              ← Önceki
            </button>
            <span style={{ padding: '6px 12px', fontSize: '13px', color: colors.textSecondary }}>
              Sayfa {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
              style={pageBtnStyle}
            >
              Sonraki →
            </button>
          </div>
        )}
      </div>

      {/* Detay Modal */}
      {selectedFatura && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => { setSelectedFatura(null); setDetayData(null); }}
        >
          <div
            style={{
              background: colors.surface,
              borderRadius: radius.xl,
              padding: '24px',
              maxWidth: '900px',
              width: '95%',
              maxHeight: '85vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>📄 Fatura Detay</h3>
              <button
                onClick={() => { setSelectedFatura(null); setDetayData(null); }}
                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: colors.textTertiary }}
              >
                ✕
              </button>
            </div>

            {/* Fatura bilgileri */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
              <div>
                <label style={{ fontSize: '11px', color: colors.textTertiary, textTransform: 'uppercase' }}>Fatura No</label>
                <div style={{ fontWeight: 600 }}>{selectedFatura.invoiceId || selectedFatura.invoiceNumber || selectedFatura.number || selectedFatura.no || '-'}</div>
              </div>
              <div>
                <label style={{ fontSize: '11px', color: colors.textTertiary, textTransform: 'uppercase' }}>Tarih</label>
                <div>{formatDate(selectedFatura.issueDate || selectedFatura.date || '')}</div>
              </div>
              <div>
                <label style={{ fontSize: '11px', color: colors.textTertiary, textTransform: 'uppercase' }}>Cari</label>
                <div>{selectedFatura.supplier || selectedFatura.senderTitle || selectedFatura.receiverTitle || selectedFatura.firmName || '-'}</div>
              </div>
              <div>
                <label style={{ fontSize: '11px', color: colors.textTertiary, textTransform: 'uppercase' }}>Toplam</label>
                <div style={{ fontWeight: 600, color: '#1976d2' }}>{formatCurrency(selectedFatura.amount || selectedFatura.totalAmount || selectedFatura.grandTotal || selectedFatura.totalTL || 0)}</div>
              </div>
            </div>

            {/* Fatura Kalemleri */}
            {detayLoading && (
              <div style={{ textAlign: 'center', padding: '20px', color: colors.textSecondary }}>⏳ Kalemler yükleniyor...</div>
            )}
            {detayData && <FaturaKalemleri detayData={detayData} formatCurrency={formatCurrency} />}
          </div>
        </div>
      )}
    </div>
  );
};

// Styles
const thStyle = {
  padding: '10px 12px',
  textAlign: 'left',
  fontSize: '12px',
  fontWeight: 600,
  color: colors.textSecondary,
  whiteSpace: 'nowrap'
};

const tdStyle = {
  padding: '10px 12px',
  fontSize: '13px',
  color: '#333'
};

const quickBtnStyle = {
  padding: '6px 14px',
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  background: colors.surface,
  cursor: 'pointer',
  fontSize: '12px',
  fontWeight: 500,
  color: '#555'
};

const dateInputStyle = {
  padding: '6px 10px',
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  fontSize: '13px'
};

const pageBtnStyle = {
  padding: '6px 14px',
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  background: colors.surface,
  cursor: 'pointer',
  fontSize: '12px'
};

export default LogoFaturalarPage;
